import { DateTime } from "luxon"
import { datetime, RRule, Weekday } from "rrule"
import { WeekDays, WeekDaysType } from "../../../../../modules/merchant/admin/productGroup/productGroupTypes"
import { RepeatOrderFormValues } from "../drawer/meetingDetails/RepeatOrderDialog"
import {
    DeliveryLocationInfo,
    DeliveryLocationType,
    MeetingServiceOrderDetailsDelivery,
} from "../../../dialogs/receiptConfirmOrderDialog/receiptConfirmOrderDialogTypes"
import {
    CreateOrderDialogType,
    MeetingServicePriceListState,
    OrderLineType,
    SatelliteDetails,
    SelectedPriceListItem,
} from "../../createOrderTypes"
import { BuyerParty } from "../../../../../modules/payments/orderTypes"
import {
    DeliveryTimesField,
    DeliveryFulfillmentType,
    DynamicConfigKey,
    isProductWishOrderLine,
    MeetingServiceTabValues,
} from "../form/formTypes"
import { CreditNoteType } from "../../../dialogs/creditNoteDialog/helpers"

export const DIALOG_TO_CREDIT_ACTION = {
    [CreateOrderDialogType.ORDER]: undefined,
    [CreateOrderDialogType.CREDIT_NOTE]: CreditNoteType.CREDIT,
    [CreateOrderDialogType.SUPPLEMENTARY_ORDER]: CreditNoteType.DEBIT,
} as const satisfies Record<CreateOrderDialogType, CreditNoteType | undefined>

export function mapToDeliveries(orderDetails: MeetingServiceTabValues): MeetingServiceOrderDetailsDelivery[] {
    const deliveriesField: DeliveryTimesField | undefined = orderDetails[DynamicConfigKey.MULTIPLE_DELIVERIES]

    if (!deliveriesField) {
        return []
    }
    const { deliveryDate, deliveryTimes } = deliveriesField

    const seats = orderDetails[DynamicConfigKey.NUMBER_OF_SEATS]
    const satellite: SatelliteDetails | null = orderDetails[DynamicConfigKey.SATELLITES]
    const type: DeliveryFulfillmentType = orderDetails[DynamicConfigKey.DELIVERY_TYPE]
    const initPriceList: MeetingServicePriceListState["item"] = orderDetails["initPriceList"]
    const priceList: SelectedPriceListItem = orderDetails["priceList"].item!

    const deliveryLocation: DeliveryLocationInfo | undefined =
        type === DeliveryFulfillmentType.DELIVERY
            ? orderDetails[DynamicConfigKey.DELIVERY_LOCATIONS]
                ? {
                      type: DeliveryLocationType.RESOURCE,
                      resourceId: Number(orderDetails[DynamicConfigKey.DELIVERY_LOCATIONS]),
                  }
                : orderDetails[DynamicConfigKey.OTHER_DELIVERY_LOCATION]
                  ? {
                        type: DeliveryLocationType.OTHER_LOCATION,
                        name: String(orderDetails[DynamicConfigKey.OTHER_DELIVERY_LOCATION]),
                    }
                  : undefined
            : undefined

    const hasPriceListMismatch = !!initPriceList && initPriceList.priceListId !== priceList.priceListId

    return deliveryTimes
        .filter((timeSlot) => timeSlot.orderLines.length > 0)
        .map(({ time, orderLines }) => {
            const filteredOrderLines = orderLines.filter((orderLine) => (orderLine.quantity ?? 0) !== 0)
            const mappedOrderLines = filteredOrderLines.map((orderLine) => {
                if (isProductWishOrderLine(orderLine)) {
                    return {
                        type: DynamicConfigKey.PRODUCT_WISH,
                        productName: orderLine.name,
                        items: orderLine.quantity,
                        price: orderLine.price,
                        buyerParty: BuyerParty.COMPANY,
                    }
                }

                if (orderLine.isExtraService) {
                    return {
                        type: OrderLineType.EXTRA_SERVICE,
                        productId: orderLine.id,
                        items: 1,
                        buyerParty: BuyerParty.COMPANY,
                    }
                }

                return {
                    productId: orderLine.id,
                    items: orderLine.quantity,
                    buyerParty: BuyerParty.COMPANY,
                    ...((hasPriceListMismatch && orderLine?.priceListId) ||
                    (orderLine?.priceListId && orderLine?.priceListId !== priceList.priceListId)
                        ? { priceListId: orderLine.priceListId }
                        : {}),
                }
            })
            return {
                type,
                seats,
                deliveryTime: DateTime.fromISO(`${deliveryDate}T${time}`).toISO({
                    suppressSeconds: true,
                    includeOffset: false,
                }),
                ...(satellite ? { satellite: { id: satellite?.id } } : {}),
                ...(deliveryLocation ? { deliveryLocation } : {}),
                orderLines: mappedOrderLines,
            }
        })
}

const weekDayToRRuleMap: Record<WeekDays, Weekday> = {
    [WeekDays.MONDAY]: RRule.MO,
    [WeekDays.TUESDAY]: RRule.TU,
    [WeekDays.WEDNESDAY]: RRule.WE,
    [WeekDays.THURSDAY]: RRule.TH,
    [WeekDays.FRIDAY]: RRule.FR,
    [WeekDays.SATURDAY]: RRule.SA,
    [WeekDays.SUNDAY]: RRule.SU,
}

function formatRecurrencyDateTime(recurrencyDateTime: DateTime) {
    return datetime(
        recurrencyDateTime.year,
        recurrencyDateTime.month,
        recurrencyDateTime.day,
        recurrencyDateTime.hour,
        recurrencyDateTime.minute,
    )
}

function mapWeekDaysToByWeekday(weekDays: string[]): Weekday[] {
    // as WeekDaysType because we have ['Wednesday', ...] which is not valid WeekDaysType
    return weekDays.map((day) => day.toUpperCase() as WeekDaysType).map((dayKey) => weekDayToRRuleMap[WeekDays[dayKey]])
}

function hasValidDates(values: RepeatOrderFormValues): boolean {
    const hasStart = values.startDate !== null
    const requiresEnd = values.endType === "on" || values.endType === "after"
    const hasEnd = !requiresEnd || values.endDate !== null

    return hasStart && hasEnd
}

export function makeRecurrencyICalFormat(recurrency: RepeatOrderFormValues) {
    if (!hasValidDates(recurrency)) return ""

    return new RRule({
        freq: recurrency.repeatType === "day" ? RRule.DAILY : RRule.WEEKLY,
        ...(recurrency.endType === "on" && recurrency.endDate
            ? { until: formatRecurrencyDateTime(recurrency.endDate) }
            : { count: recurrency.repeatCount }),
        ...(recurrency.repeatType !== "day" && { byweekday: mapWeekDaysToByWeekday(recurrency.weekDays) }),
        dtstart: formatRecurrencyDateTime(recurrency.startDate!),
    }).toString()
}
